// המשימה הבאה — as a thin STRIP, not a card (design "מסילה קבועה").
//
// This replaces the old OverviewNextTaskPanel card outright. A card with a
// title bar, 20px of padding and a shadow, wrapped around "one task title +
// one link", is exactly the card tax the redesign removes: it made a two-line
// fact compete visually with the AI summary and the activity feed. As a single
// tinted row at the top of the working column it reads as what it is — the one
// thing on this screen that wants an action right now.
//
// The data path is UNCHANGED from the panel it replaces: the next pending
// eventType='task' calendar event for this contact comes from the app-wide
// CalendarContext listener (no extra query), the checkbox completes it in place
// via the event's doc path (the CalendarPage-approved write), and overdue tasks
// get the באיחור state.
import { useEffect, useState } from "react";
import { Box, Button, Checkbox, Skeleton, Stack, Typography } from "@material-ui/core";
import { TaskAltOutlined } from "@material-ui/icons";
import { alpha } from "@material-ui/core/styles";
import { startOfDay } from "date-fns";
import { useSnackbar } from "notistack5";
import { db } from "firebaseConfig";
import Label from "components/Label";
import useCalendar from "hooks/useCalendar";
import useSettings from "hooks/useSettings";
import { fDate } from "utils/formatTime";
import { useContactSurfaceDown } from "../../contactSurface";
import { useSwitchTab } from "./useSwitchTab";
// ONE contact-matching rule for tasks across the page (attendee phones AND the
// Cloud-Function-stamped contactId) — keeps this strip's count in agreement
// with the tasks tab/badge for the same contact.
import { isEventOfContact } from "../tasks/contactTasksData";
import type { CalendarEvent, RealEstateContact } from "../../../../../../@types/sharedSchema";
import type { ReactNode } from "react";

type OverviewTaskStripProps = {
  contact: RealEstateContact;
};

/**
 * The strip shell. `tone="active"` is the warning tint used when there IS
 * something to do; `tone="quiet"` is the flat dashed outline used for the
 * loading and empty states — an empty task list is not a warning, and tinting
 * it would make every brand-new lead's overview open on a false alarm.
 */
function StripShell({
  tone,
  children
}: {
  tone: "active" | "quiet";
  children: ReactNode;
}): JSX.Element {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        gap: 1,
        px: 1.25,
        py: 0.75,
        minHeight: 48,
        borderRadius: 1.5,
        border: "1px solid",
        ...(tone === "active"
          ? {
              borderColor: (theme) => alpha(theme.palette.warning.main, 0.32),
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12)
            }
          : {
              borderStyle: "dashed",
              borderColor: "divider",
              bgcolor: "transparent"
            })
      }}
    >
      {children}
    </Stack>
  );
}

export function OverviewTaskStrip({ contact }: OverviewTaskStripProps): JSX.Element {
  const switchTab = useSwitchTab();
  const { enqueueSnackbar } = useSnackbar();
  const { events, isFetchingEvents, initiateLoadEvents } = useCalendar();
  const { themeDirection } = useSettings();
  const isRTL = themeDirection === "rtl";
  // The SURFACE, not the window: this strip is one row of a fixed-width shell, and in the
  // CRM side panel a viewport query kept the date column that the 420px column has no room
  // for — which is what pushed the task title into an ellipsis two words in.
  const isNarrow = useContactSurfaceDown("sm");

  // CalendarContext only starts its events listener after some consumer kicks
  // it (dedups via a boolean — same idempotent pattern as useContactTasks).
  // Without this, the standalone route / new-tab open never loads events and
  // this strip skeletons forever.
  useEffect(() => {
    initiateLoadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [completingId, setCompletingId] = useState<string | null>(null);
  // Optimistic local mark — the context listener confirms shortly after.
  const [locallyDoneIds, setLocallyDoneIds] = useState<ReadonlySet<string>>(new Set());

  const pendingTasks = (events ?? [])
    .filter(
      (event) =>
        event.eventType === "task" &&
        event.status !== "done" &&
        !locallyDoneIds.has(event.id ?? "") &&
        isEventOfContact(event, contact)
    )
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  const nextTask = pendingTasks[0];
  const isOverdue = nextTask
    ? startOfDay(new Date(nextTask.start)) < startOfDay(new Date())
    : false;

  const handleComplete = async (task: CalendarEvent): Promise<void> => {
    const taskId = task.id;
    const taskPath = task.path;
    if (!taskPath || !taskId) return;
    try {
      setCompletingId(taskId);
      setLocallyDoneIds((prev) => new Set(prev).add(taskId));
      await db.doc(taskPath).update({ status: "done" });
    } catch (error) {
      console.error("OverviewTaskStrip: failed completing task", error);
      setLocallyDoneIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      enqueueSnackbar("סימון המשימה נכשל", { variant: "error", autoHideDuration: 4000 });
    } finally {
      setCompletingId(null);
    }
  };

  // The "all tasks" jump is present in EVERY state — it is the strip's only
  // route into the tasks tab, and a contact with no task is exactly when the
  // realtor most wants to go add one.
  // NOT color="warning": palette.warning.main is #FFC107, which as button text
  // on the strip's own light tint is unreadable. warning.dark (#B78103) is the
  // readable member of that ramp — same convention the AI card uses with
  // info.dark.
  const allTasksButton = (
    <Button
      size="small"
      variant="text"
      color="inherit"
      onClick={() => switchTab("tasks")}
      sx={{
        flexShrink: 0,
        minWidth: 0,
        px: 0.75,
        color: nextTask ? "warning.dark" : "text.secondary"
      }}
    >
      {pendingTasks.length > 1 ? `כל המשימות (${pendingTasks.length}) ←` : "כל המשימות ←"}
    </Button>
  );

  if (nextTask) {
    return (
      <StripShell tone="active">
        <Checkbox
          size="small"
          checked={false}
          disabled={completingId === nextTask.id || !nextTask.path}
          onChange={() => void handleComplete(nextTask)}
          sx={{ p: 0.5, flexShrink: 0 }}
          inputProps={{ "aria-label": "סימון המשימה כבוצעה" }}
        />
        {/* Task titles are free user text and this is a one-line strip: clip
            with an ellipsis (full text stays available on hover) rather than
            letting a long title wrap the row into a block. */}
        <Typography
          variant="body2"
          dir="auto"
          title={nextTask.title}
          sx={{
            fontWeight: 600,
            flexGrow: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {nextTask.title}
        </Typography>
        {isOverdue && (
          <Label color="error" variant="ghost" sx={{ flexShrink: 0 }}>
            באיחור
          </Label>
        )}
        {/* The date is the first thing to go on a narrow surface — באיחור
            already carries the urgency, and the title needs the room. */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flexShrink: 0, display: isNarrow ? "none" : "block" }}
        >
          {fDate(nextTask.start, isRTL)}
        </Typography>
        {allTasksButton}
      </StripShell>
    );
  }

  if (isFetchingEvents) {
    return (
      <StripShell tone="quiet">
        <Skeleton variant="circular" width={20} height={20} sx={{ flexShrink: 0, ml: 0.5 }} />
        <Skeleton variant="text" sx={{ flexGrow: 1, maxWidth: 220 }} />
      </StripShell>
    );
  }

  return (
    <StripShell tone="quiet">
      <Box
        sx={{
          width: 30,
          display: "flex",
          justifyContent: "center",
          flexShrink: 0,
          color: "text.disabled"
        }}
      >
        <TaskAltOutlined sx={{ fontSize: 18 }} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, minWidth: 0 }}>
        אין משימה פתוחה ללקוח
      </Typography>
      {allTasksButton}
    </StripShell>
  );
}
